import { collection, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { requireUid } from '@/utils/authHelpers';
import { captureError } from '@/utils/captureError';
import { getDirectImageUrl } from '@/utils/imageUrl';
import type { Wine, SharedCellar, SharedWine } from '@/types';
import { SHARED_WINE_KEYS } from '@/types';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function wineToSharedWine(wine: Wine): SharedWine {
  const shared: Partial<SharedWine> = {};
  for (const key of SHARED_WINE_KEYS) {
    const val = wine[key];
    if (val !== undefined) (shared as any)[key] = val;
  }
  // Resolve image URLs now (while authenticated) so legacy paths get
  // rewritten to user-scoped paths that work without auth context
  shared.imageUrl = getDirectImageUrl(wine.resolvedImageUrl || wine.imageUrl);
  shared.thumbnailUrl = getDirectImageUrl(wine.thumbnailUrl || wine.resolvedImageUrl || wine.imageUrl);
  shared.resolvedImageUrl = shared.imageUrl;
  return shared as SharedWine;
}

export async function createShare(
  wines: Wine[],
  totalBottles: number,
  ownerDisplayName: string,
): Promise<string | null> {
  try {
    const uid = requireUid();
    const shareRef = doc(collection(db, 'public_shares'));
    const now = Date.now();
    const payload: SharedCellar & { ownerId: string } = {
      ownerId: uid,
      ownerDisplayName,
      totalBottles,
      totalLabels: wines.length,
      wines: wines.map(wineToSharedWine),
      createdAt: now,
      expiresAt: now + THIRTY_DAYS_MS,
    };
    await setDoc(shareRef, payload);
    return shareRef.id;
  } catch (err) {
    captureError(err, { context: 'share_cellar', action: 'create' });
    return null;
  }
}

export async function getShare(shareId: string): Promise<SharedCellar | null> {
  try {
    const snap = await getDoc(doc(db, 'public_shares', shareId));
    if (!snap.exists()) return null;
    const data = snap.data() as SharedCellar & { ownerId?: string };
    if (data.expiresAt < Date.now()) return null;
    // Strip ownerId — only stored for audit
    const { ownerId: _, ...cellar } = data;
    return cellar as SharedCellar;
  } catch (err) {
    captureError(err, { context: 'share_cellar', action: 'get' });
    return null;
  }
}
